import { Add, Remove } from '@mui/icons-material';
import { Box, Input } from '@mui/material';
import darkTheme from '@shared/themes/darkTheme';
import { useEffect, useState } from 'react';
import { useWidgetStore } from '../../../common/store/AppStore';
import { LadderStore } from './context/LadderStore';

const quantities = [1, 5, 10, 25, 50, 100];

const QuickButton = (props: { quantity: number; onClick: (e: React.MouseEvent) => void }) => {
  const { quantity, onClick } = props;

  return (
    <Box
      sx={{
        lineHeight: '2.6em',
        borderRadius: '1em',
        minWidth: '3em',
        cursor: 'pointer',
        background: '#333',
        textAlign: 'center',
        '&:hover': { background: '#555' },
      }}
      key={quantity}
      onClick={onClick}
    >
      <span style={{ userSelect: 'none' }}>{quantity}</span>
    </Box>
  );
};

export type LadderQuantityEntryProps = {
  id: string;
};

export const LadderQuantityEntry = (props: LadderQuantityEntryProps) => {
  const { orderAmount, setOrderAmount } = useWidgetStore<LadderStore>(props.id);

  const [customAmount, setCustomAmount] = useState<string>(orderAmount.toFixed(0));
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const [shiftPressed, setShiftPressed] = useState<boolean>(false);
  const [ctrlPressed, setCtrlPressed] = useState<boolean>(false);

  const validateInput = () => {
    const asNumber = Number(customAmount);
    if (!isNaN(asNumber)) {
      setOrderAmount(asNumber);
    } else {
      setCustomAmount(orderAmount.toFixed(0));
    }
    setIsEditing(false);
  };

  useEffect(() => {
    const keyDownListener = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        if (!shiftPressed) setShiftPressed(true);
      }
      if (e.key === 'Control' || e.key === 'Super') {
        if (!ctrlPressed) setCtrlPressed(true);
      }
    };

    const keyUpListener = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setShiftPressed(false);
      }
      if (e.key === 'Control' || e.key === 'Super') {
        setCtrlPressed(false);
      }
    };

    const blurListener = () => {
      setShiftPressed(false);
      setCtrlPressed(false);
    };

    window.addEventListener('keydown', keyDownListener);
    window.addEventListener('keyup', keyUpListener);
    window.addEventListener('blur', blurListener);

    return () => {
      window.removeEventListener('keydown', keyDownListener);
      window.removeEventListener('keyup', keyUpListener);
      window.removeEventListener('blur', blurListener);
    };
  });

  let controlIcon: JSX.Element | string = '';

  if (shiftPressed) {
    controlIcon = <Add fontSize={'medium'} />;
  } else if (ctrlPressed) {
    controlIcon = <Remove fontSize={'medium'} />;
  }

  const inputStyle = {
    width: '5em',
    paddingLeft: '.5em',
  };

  return (
    <Box sx={{ padding: '.5em 0', display: 'flex', gap: '.25em', width: '100%', flexWrap: 'nowrap' }}>
      {quantities.map((q, i) => {
        return (
          <QuickButton
            quantity={q}
            key={q}
            onClick={e => {
              let newValue = q;
              if (e.shiftKey) {
                newValue = orderAmount + q;
              } else if (e.ctrlKey) {
                newValue = Math.max(orderAmount - q, 1);
              }

              setOrderAmount(newValue);
              setCustomAmount(newValue.toFixed(0));
            }}
          />
        );
      })}

      <div style={{ height: '100%', flexGrow: 1, paddingTop: '.5em', display: 'flex', justifyContent: 'center' }}>{controlIcon}</div>

      <div style={{ justifySelf: 'flex-end', display: 'flex', flexDirection: 'row' }}>
        <div style={{ height: '100%', lineHeight: '2.6em', marginRight: '.5em', userSelect: 'none' }}>Quantity: </div>
        {isEditing ? (
          <Input
            value={customAmount}
            inputMode="numeric"
            type="number"
            autoFocus={true}
            sx={{ ...inputStyle }}
            onChange={e => {
              setCustomAmount(e.target.value);
            }}
            onFocus={e => {
              e.target.select();
            }}
            onBlur={() => {
              validateInput();
            }}
            onKeyUp={e => {
              if (e.key === 'Enter') validateInput();
            }}
          ></Input>
        ) : (
          <div
            style={{ ...inputStyle, height: '100%', backgroundColor: darkTheme.palette.background.lighter, cursor: 'pointer' }}
            onClick={() => setIsEditing(true)}
          >
            <span style={{ verticalAlign: 'middle', lineHeight: '2.6em' }}>{orderAmount}</span>
          </div>
        )}
      </div>
    </Box>
  );
};
